// Sentry browser error monitoring.
//
// Error monitoring ONLY — NO session replay. Replay records the
// rendered screen; on a system holding SSNs / financial data that
// would ship PII to a third party. It is off by design (we never add
// the replay integration, and the sample rates are 0).
//
// The DSN below is a *browser* DSN — public by design: it ships in the
// client bundle and only permits SENDING events to this project, not
// reading them. Safe to commit. One Sentry project serves both the web
// app (tagged surface:web) and the API worker (surface:worker).

import * as Sentry from "@sentry/react";

import { scrubSentryEvent } from "../../../shared/observability/sentry-scrub";

const DSN =
  "https://4e785e323ceb527a663d325f1af689d1@o4511528019558400.ingest.us.sentry.io/4511528071135232";

export function initSentry(): void {
  if (typeof window === "undefined") return;
  // Local dev (vite dev server) must not pollute production error
  // tracking — HMR remounts and half-typed code produce noise events
  // that bury real signals (2026-06-09 audit). Deployed builds run in
  // production mode, so this gate only silences localhost.
  if (import.meta.env.DEV) return;
  Sentry.init({
    dsn: DSN,
    // Pin our own release string. Left unset, the SDK auto-detects one
    // from a page-global (window.SENTRY_RELEASE) — and a visitor's
    // browser extension can set that same global on OUR page (content
    // scripts share window in some extension frameworks), which is
    // exactly how a real production error got tagged
    // "browserflow-extension@1.0.6" in Sentry (2026-08-28, issue
    // JAVASCRIPT-REACT-3V) even though we ship nothing by that name.
    // An explicit release here always wins over auto-detection.
    release: "stewardship-os-web",
    // Error monitoring only — no performance tracing, no replay.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // Browser-EXTENSION noise filter (2026-08-20). A visitor's extension
    // (password manager, coupon tool, …) that crashes on one of our pages
    // surfaces as an unhandled rejection OUR GlobalHandlers capture — e.g.
    // "Invalid call to runtime.sendMessage(). Tab not found." on the
    // public intake form. `runtime.sendMessage` is the WebExtension API;
    // nothing in this codebase calls it. Drop these at the SDK so the
    // production feed only carries errors we can act on. Real errors from
    // our own bundles are unaffected: ignoreErrors matches only these
    // extension-API message shapes, and denyUrls only frames whose script
    // lives at an extension:// origin.
    // Same idea for iOS in-app browsers (Instagram, Gmail, link previews):
    // the HOST app's injected bridge script rejects with "WKWebView API
    // client did not respond to this postMessage" (2026-10-06, public form).
    // Nothing in this codebase calls it and the page keeps working.
    ignoreErrors: [
      /runtime\.sendMessage/i,
      /Extension context invalidated/i,
      /WKWebView API client did not respond/i,
    ],
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-(web-)?extension:\/\//i],
    initialScope: { tags: { surface: "web" } },
    beforeSend: (event) => scrubSentryEvent(event),
    beforeBreadcrumb: (crumb) => {
      // Drop request/response bodies from network breadcrumbs — they
      // can carry record data. (URLs use opaque UUIDs, not PII.)
      if (
        (crumb.category === "xhr" || crumb.category === "fetch") &&
        crumb.data &&
        typeof crumb.data === "object"
      ) {
        const d = crumb.data as Record<string, unknown>;
        delete d.body;
        delete d.response;
      }
      return crumb;
    },
  });
}
